import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronRight, ChevronDown, ChevronUp, PanelLeftClose, PanelLeft, Grid3X3, LayoutDashboard } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { useSidebar } from '../../../context/SidebarContext';
import { getAllModules, getModuleById } from '../../../config/moduleConfig';
import { useBranding } from '../../../context/BrandingContext';

/**
 * AdminSidebar Component
 *
 * A collapsible sidebar navigation for the admin panel with menu items,
 * active state indicators, badges, and user information.
 *
 * @param {Object} props
 * @param {Array} props.menuItems - Array of menu item objects
 * @param {Array} props.bottomMenuItems - Array of bottom menu item objects
 * @param {Object} props.branding - Branding configuration
 * @param {boolean} props.defaultOpen - Initial sidebar state (default: true)
 */
export default function AdminSidebar({
  menuItems = [],
  bottomMenuItems = [],
  branding = {
    logo: 'Restaurant',
    subtitle: 'Admin Portal',
    color: 'teal'
  },
  defaultOpen = true
}) {
  const { isCollapsed, setIsCollapsed, hasProvider } = useSidebar();
  const brandingConfig = useBranding();
  // Use context state if provider is available, otherwise use local state
  const [localSidebarOpen, setLocalSidebarOpen] = useState(defaultOpen);
  const sidebarOpen = hasProvider ? !isCollapsed : localSidebarOpen;
  const setSidebarOpen = (open) => {
    if (hasProvider) {
      setIsCollapsed(!open);
    }
    setLocalSidebarOpen(open);
  };

  const [openMenus, setOpenMenus] = useState({});
  const [hoveredMenu, setHoveredMenu] = useState(null);
  const [hideTimeout, setHideTimeout] = useState(null);
  const [showModuleSwitcher, setShowModuleSwitcher] = useState(false);
  const moduleSwitcherRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, hasModuleAccess, hasPermission } = useAuth();

  // Get current module (backoffice) and all switchable modules (subscription + permission gated)
  const currentModule = getModuleById('BACKOFFICE');
  const modulePermissionMap = {
    BACKOFFICE: 'back_office', INVENTORY: 'inventory', REPORTS: 'reports',
    CRM: 'crm', HRM: 'hrm', QR_MENU: 'qr_menu', POS: 'pos',
    KDS: 'kds', CAPTAIN_APP: 'captain_app', INTEGRATIONS: 'integrations',
    AI_INSIGHTS: 'ai_insights',
  };
  const switchableModules = getAllModules().filter((m) => {
    if (m.isComingSoon) return false;
    if (!hasModuleAccess(m.id)) return false;
    const perm = modulePermissionMap[m.id];
    if (!perm) return false;
    return hasPermission(perm);
  });

  const canSeeItem = (item) => {
  if (!item.permission) return true;
  return hasPermission(item.permission);
};

const filteredMenuItems = useMemo(() => {
  return menuItems.reduce((acc, item) => {
    const hasChildren = Array.isArray(item.children) && item.children.length > 0;
    if (hasChildren) {
      const visibleChildren = item.children.filter(canSeeItem);
      if (visibleChildren.length > 0) acc.push({ ...item, children: visibleChildren });
    } else {
      if (canSeeItem(item)) acc.push(item);
    }
    return acc;
  }, []);
}, [menuItems, hasPermission]);

const filteredBottomMenuItems = useMemo(() => {
  return bottomMenuItems.reduce((acc, item) => {
    const hasChildren = Array.isArray(item.children) && item.children.length > 0;
    if (hasChildren) {
      const visibleChildren = item.children.filter(canSeeItem);
      if (visibleChildren.length > 0) acc.push({ ...item, children: visibleChildren });
    } else {
      if (canSeeItem(item)) acc.push(item);
    }
    return acc;
  }, []);
}, [bottomMenuItems, hasPermission]);

  // Close module switcher when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (moduleSwitcherRef.current && !moduleSwitcherRef.current.contains(event.target)) {
        setShowModuleSwitcher(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-collapse sidebar when on Settings or Inventory page (since they have their own sidebar)
  useEffect(() => {
    const isOnSettingsPage = location.pathname.startsWith('/admin/settings');
    const isOnInventoryPage = location.pathname.startsWith('/admin/inventory');
    if (isOnSettingsPage || isOnInventoryPage) {
      setSidebarOpen(false);
    }
  }, [location.pathname]);

  // Initialize openMenus state for items with active children (both menuItems and bottomMenuItems)
  useEffect(() => {
    const newOpenMenus = {};

    // Check main menu items
    menuItems.forEach((item) => {
      if (item.children && item.children.length > 0) {
        const hasActiveChild = item.children.some(
          (child) => location.pathname === child.path
        );
        if (hasActiveChild) {
          newOpenMenus[item.label] = true;
        }
      }
    });

    // Check bottom menu items
    bottomMenuItems.forEach((item) => {
      if (item.children && item.children.length > 0) {
        const hasActiveChild = item.children.some(
          (child) => location.pathname === child.path
        );
        if (hasActiveChild) {
          newOpenMenus[item.label] = true;
        }
      }
    });

    if (Object.keys(newOpenMenus).length > 0) {
      setOpenMenus((prev) => ({ ...prev, ...newOpenMenus }));
    }
  }, [location.pathname]);

  // Color scheme mapping
  const colorSchemes = {
    blue: {
      gradient: 'from-blue-500 to-blue-600',
      active: 'bg-blue-50 text-blue-600',
      indicator: 'bg-blue-600',
      avatar: 'bg-blue-100 text-blue-600'
    },
    teal: {
      gradient: 'from-teal-500 to-teal-600',
      active: 'bg-teal-50 text-teal-600',
      indicator: 'bg-teal-600',
      avatar: 'bg-teal-100 text-teal-600'
    },
    purple: {
      gradient: 'from-purple-500 to-purple-600',
      active: 'bg-purple-50 text-purple-600',
      indicator: 'bg-purple-600',
      avatar: 'bg-purple-100 text-purple-600'
    },
    red: {
      gradient: 'from-red-500 to-red-600',
      active: 'bg-red-50 text-red-600',
      indicator: 'bg-red-600',
      avatar: 'bg-red-100 text-red-600'
    }
  };

  const colors = colorSchemes[branding.color] || colorSchemes.blue;

  // Get user initials for avatar
  const getUserInitial = () => {
    if (user?.name) {
      return user.name.charAt(0).toUpperCase();
    }
    return 'U';
  };

  // Toggle sidebar
  const handleToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Handle module switch
  const handleModuleSwitch = (targetModule) => {
    setShowModuleSwitcher(false);
    const path = targetModule.id === 'BACKOFFICE' ? '/admin/dashboard' : targetModule.basePath;
    navigate(path);
  };

  // Handle menu item click
  const handleMenuClick = (path, parentLabel = null) => {
    // Auto-collapse sidebar when navigating to Settings or Inventory
    if (path === '/admin/settings' || path === '/admin/inventory') {
      setSidebarOpen(false);
    }
    // Keep parent menu open when clicking a child
    if (parentLabel) {
      setOpenMenus((prev) => ({ ...prev, [parentLabel]: true }));
    }
    navigate(path);
  };

  // Check if menu item is active
  const isActive = (path) => {
    return location.pathname === path;
  };

  // Handle popover show with delay
  const handlePopoverShow = (menuLabel) => {
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      setHideTimeout(null);
    }
    setHoveredMenu(menuLabel);
  };

  // Handle popover hide with delay
  const handlePopoverHide = () => {
    const timeout = setTimeout(() => {
      setHoveredMenu(null);
    }, 200); // 200ms delay before hiding
    setHideTimeout(timeout);
  };

  // Cancel hide when mouse enters popover
  const handlePopoverEnter = () => {
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      setHideTimeout(null);
    }
  };

  return (
    <aside
      className={`${
        sidebarOpen ? 'w-60' : 'w-20'
      } bg-white border-r border-slate-200 transition-all duration-300 flex flex-col shadow-sm flex-shrink-0`}
    >
      {/* Module Switcher Section */}
      <div className="border-b border-slate-200 p-4 relative" ref={moduleSwitcherRef}>
        {/* Module Icon with Switch Indicator */}
        <button
          onClick={() => setShowModuleSwitcher(!showModuleSwitcher)}
          className={`w-full flex items-center gap-3 rounded-lg p-2 transition-all group ${
            showModuleSwitcher
              ? 'bg-slate-100'
              : 'hover:bg-slate-50'
          }`}
          title="Click to switch module"
        >
          {/* Module Icon */}
          <div className="relative">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-sm transition-transform group-hover:scale-105">
              <LayoutDashboard className="w-5 h-5 text-white" />
            </div>
            {/* Small switch indicator */}
            <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center shadow-sm transition-all ${
              showModuleSwitcher
                ? 'bg-teal-500 text-white'
                : 'bg-white border border-slate-200 text-slate-400 group-hover:border-teal-300 group-hover:text-teal-500'
            }`}>
              {showModuleSwitcher ? (
                <ChevronUp className="w-3 h-3" />
              ) : (
                <Grid3X3 className="w-3 h-3" />
              )}
            </div>
          </div>

          {/* Module Name - only when expanded */}
          {sidebarOpen && (
            <div className="flex-1 min-w-0 text-left">
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-bold text-slate-900 leading-tight truncate">
                  Back Office
                </h2>
                <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${showModuleSwitcher ? 'rotate-180' : ''}`} />
              </div>
              <p className="text-[11px] text-slate-500 truncate">
                Click to switch module
              </p>
            </div>
          )}
        </button>

        {/* Module Switcher Dropdown */}
        {showModuleSwitcher && (
          <div
            className={`absolute ${sidebarOpen ? 'left-3 right-3' : 'left-full ml-2'} top-full mt-1 bg-white rounded-xl shadow-2xl border border-slate-200 py-2 z-50 min-w-[240px]`}
          >
            <div className="px-4 py-3 border-b border-slate-100">
              <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                <Grid3X3 className="w-3.5 h-3.5" />
                <span>Switch Module</span>
              </div>
            </div>
            <div
              className="py-2 max-h-[400px] overflow-y-auto"
              style={{
                scrollbarWidth: 'thin',
                scrollbarColor: '#cbd5e1 transparent',
              }}
            >
              {switchableModules.map((m) => {
                const MIcon = m.icon;
                const isCurrentModule = m.id === 'backoffice';
                return (
                  <button
                    key={m.id}
                    onClick={() => handleModuleSwitch(m)}
                    disabled={isCurrentModule}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all ${
                      isCurrentModule
                        ? 'bg-teal-50 border-l-2 border-teal-500'
                        : 'hover:bg-slate-50 border-l-2 border-transparent hover:border-slate-300'
                    }`}
                  >
                    <div
                      className={`w-9 h-9 bg-gradient-to-br ${m.gradient} rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm`}
                    >
                      <MIcon className="w-4.5 h-4.5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-semibold truncate ${isCurrentModule ? 'text-teal-700' : 'text-slate-700'}`}>
                        {m.name}
                      </p>
                      <p className="text-xs text-slate-400 truncate">{m.description}</p>
                    </div>
                    {isCurrentModule && (
                      <span className="text-[10px] font-semibold text-teal-600 bg-teal-100 px-2 py-0.5 rounded-full">
                        Active
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            {/* Quick access to all modules */}
            <div className="border-t border-slate-100 pt-2 mt-1 px-3">
              <button
                onClick={() => {
                  setShowModuleSwitcher(false);
                  navigate('/select-role');
                }}
                className="w-full flex items-center justify-center gap-2 px-3 py-2.5 text-xs font-semibold text-teal-600 hover:text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-lg transition-colors"
              >
                <Grid3X3 className="w-3.5 h-3.5" />
                View All Modules
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Sidebar Toggle Button */}
      <div className="px-3 py-2 border-b border-slate-200">
        <button
          onClick={handleToggle}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-1"
          title={sidebarOpen ? 'Collapse Sidebar' : 'Expand Sidebar'}
          aria-label={sidebarOpen ? 'Collapse Sidebar' : 'Expand Sidebar'}
        >
          {sidebarOpen ? (
            <>
              <PanelLeftClose className="w-5 h-5" />
              <span className="text-sm font-medium">Collapse</span>
            </>
          ) : (
            <PanelLeft className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* Menu Items Section */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto custom-scrollbar">
        {/* Main Menu Items */}
        {filteredMenuItems.map((item) => {
          const Icon = item.icon;
          const hasChildren = Array.isArray(item.children) && item.children.length > 0;
          const hasActiveChild = hasChildren && item.children.some(
            (child) => location.pathname === child.path
          );
          const active = isActive(item.path) || hasActiveChild;
          const isOpen = openMenus[item.label] ?? false;
          const showPopover = !sidebarOpen && hasChildren && hoveredMenu === item.label;

          return (
            <div
              key={item.path}
              className="relative"
              onMouseEnter={() => !sidebarOpen && hasChildren && handlePopoverShow(item.label)}
              onMouseLeave={() => !sidebarOpen && hasChildren && handlePopoverHide()}
            >
              <button
                onClick={() => {
                  if (hasChildren && sidebarOpen) {
                    setOpenMenus((prev) => ({ ...prev, [item.label]: !prev[item.label] }));
                  } else if (!hasChildren) {
                    handleMenuClick(item.path);
                  }
                }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group relative focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-1 ${
                  active
                    ? colors.active
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
                title={!sidebarOpen ? item.label : ''}
                aria-label={item.label}
                aria-current={active ? 'page' : undefined}
              >
                {/* Active Indicator Bar */}
                {active && !hasChildren && (
                  <div className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 ${colors.indicator} rounded-r-full`} />
                )}

                {/* Icon */}
                <div className={`flex-shrink-0 ${active ? '' : 'group-hover:scale-110'} transition-transform`}>
                  <Icon className="w-5 h-5" />
                </div>

                {/* Label, Badge, and Arrow (when expanded) */}
                {sidebarOpen && (
                  <>
                    <span className="flex-1 text-left font-medium text-sm truncate">
                      {item.label}
                    </span>

                    {/* Badge */}
                    {item.badge && (
                      <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full font-semibold shadow-sm">
                        {item.badge}
                      </span>
                    )}

                    {/* Chevron for expandable menus */}
                    {hasChildren ? (
                      <div className="transition-transform duration-200">
                        {isOpen ? (
                          <ChevronDown className="w-4 h-4 text-slate-500" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-slate-500" />
                        )}
                      </div>
                    ) : (
                      active && <ChevronRight className="w-4 h-4 flex-shrink-0" />
                    )}
                  </>
                )}

                {/* Tooltip Badge for Collapsed State */}
                {!sidebarOpen && item.badge && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold shadow-md">
                    {item.badge}
                  </span>
                )}

                {/* Active badge indicator when collapsed and has active child */}
                {!sidebarOpen && hasActiveChild && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-teal-600 rounded-full"></span>
                )}
              </button>

              {/* SUBMENU - Expanded Sidebar */}
              {hasChildren && isOpen && sidebarOpen && (
                <div className="mt-1 ml-3 pl-3 border-l-2 border-slate-200 space-y-0.5">
                  {item.children.map((child) => {
                    const ChildIcon = child.icon;
                    const childActive = location.pathname === child.path;

                    return (
                      <button
                        key={child.path}
                        onClick={() => handleMenuClick(child.path, item.label)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group relative focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-1 ${
                          childActive
                            ? colors.active
                            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                        }`}
                        aria-current={childActive ? 'page' : undefined}
                      >
                        {/* Active Indicator Bar for submenu */}
                        {childActive && (
                          <div className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 ${colors.indicator} rounded-r-full`} />
                        )}

                        {/* Child Icon */}
                        <div className="flex-shrink-0 group-hover:scale-110 transition-transform">
                          <ChildIcon className="w-4 h-4" />
                        </div>

                        {/* Child Label */}
                        <span className="flex-1 text-left truncate">{child.label}</span>

                        {/* Active Arrow */}
                        {childActive && (
                          <ChevronRight className="w-4 h-4 flex-shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* SUBMENU POPOVER - Collapsed Sidebar */}
              {showPopover && (() => {
                const buttonElement = document.querySelector(`button[aria-label="${item.label}"]`);
                const rect = buttonElement?.getBoundingClientRect();

                return (
                  <div
                    className="fixed bg-white rounded-lg shadow-xl border border-slate-200 py-2 px-1 min-w-[200px] z-[100]"
                    style={{
                      left: `${(rect?.right || 0) + 8}px`,
                      top: `${rect?.top || 0}px`,
                    }}
                    onMouseEnter={handlePopoverEnter}
                    onMouseLeave={handlePopoverHide}
                  >
                    {/* Popover Header */}
                    <div className="px-3 py-2 border-b border-slate-100 mb-1">
                      <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                        {item.label}
                      </p>
                    </div>

                    {/* Popover Menu Items */}
                    <div className="space-y-0.5">
                      {item.children.map((child) => {
                        const ChildIcon = child.icon;
                        const childActive = location.pathname === child.path;

                        return (
                          <button
                            key={child.path}
                            onClick={() => handleMenuClick(child.path, item.label)}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 mx-1 rounded-lg text-sm font-medium transition-all duration-200 group relative ${
                              childActive
                                ? colors.active
                                : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                            }`}
                          >
                            {/* Active Indicator */}
                            {childActive && (
                              <div className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 ${colors.indicator} rounded-r-full`} />
                            )}

                            {/* Child Icon */}
                            <div className="flex-shrink-0 group-hover:scale-110 transition-transform">
                              <ChildIcon className="w-4 h-4" />
                            </div>

                            {/* Child Label */}
                            <span className="flex-1 text-left truncate">{child.label}</span>

                            {/* Active Arrow */}
                            {childActive && (
                              <ChevronRight className="w-4 h-4 flex-shrink-0" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          );
        })}

        {/* Divider (only if bottom menu items exist) */}
        {bottomMenuItems.length > 0 && (
          <div className="py-2">
            <div className="h-px bg-slate-200" />
          </div>
        )}

 {/* Bottom Menu Items */}
{filteredBottomMenuItems.map((item, index) => {
  const Icon = item.icon;
  const hasChildren = Array.isArray(item.children) && item.children.length > 0;
  const hasActiveChild = hasChildren && item.children.some(
    (child) => location.pathname === child.path
  );
  const isOpen = openMenus[item.label] ?? false;
  const showPopover = !sidebarOpen && hasChildren && hoveredMenu === item.label;

  return (
    <div
      key={item.path}
      className="relative"
      onMouseEnter={() => !sidebarOpen && hasChildren && handlePopoverShow(item.label)}
      onMouseLeave={() => !sidebarOpen && hasChildren && handlePopoverHide()}
    >
      {/* MAIN MENU BUTTON */}
      <button
        onClick={() => {
          if (hasChildren && sidebarOpen) {
            setOpenMenus((prev) => ({ ...prev, [item.label]: !prev[item.label] }));
          } else if (!hasChildren) {
            handleMenuClick(item.path);
          }
        }}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group relative focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-1 ${
          location.pathname === item.path
            ? colors.active
            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
        }`}
        title={!sidebarOpen ? item.label : ''}
        aria-label={item.label}
      >
        {/* Active Indicator Bar (only when directly on this path) */}
        {location.pathname === item.path && (
          <div className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 ${colors.indicator} rounded-r-full`} />
        )}

        {/* Icon */}
        <div className="flex-shrink-0 group-hover:scale-110 transition-transform">
          <Icon className="w-5 h-5" />
        </div>

        {/* Label and Arrow (when expanded) */}
        {sidebarOpen && (
          <>
            <span className="flex-1 text-left font-medium text-sm truncate">
              {item.label}
            </span>

            {/* Chevron Arrow for expandable menus */}
            {hasChildren && (
              <div className="transition-transform duration-200">
                {isOpen ? (
                  <ChevronDown className="w-4 h-4 text-slate-500" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-slate-500" />
                )}
              </div>
            )}
          </>
        )}

        {/* Active badge indicator when collapsed and has active child */}
        {!sidebarOpen && hasActiveChild && (
          <span className="absolute -top-1 -right-1 w-2 h-2 bg-teal-600 rounded-full"></span>
        )}
      </button>

      {/* SUBMENU - Expanded Sidebar */}
      {hasChildren && isOpen && sidebarOpen && (
        <div className="mt-1 ml-3 pl-3 border-l-2 border-slate-200 space-y-0.5">
          {item.children.map((child) => {
            const ChildIcon = child.icon;
            const childActive = location.pathname === child.path;

            return (
              <button
                key={child.path}
                onClick={() => handleMenuClick(child.path, item.label)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group relative focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-1 ${
                  childActive
                    ? colors.active
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
                aria-current={childActive ? 'page' : undefined}
              >
                {/* Active Indicator Bar for submenu */}
                {childActive && (
                  <div className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 ${colors.indicator} rounded-r-full`} />
                )}

                {/* Child Icon */}
                <div className="flex-shrink-0 group-hover:scale-110 transition-transform">
                  <ChildIcon className="w-4 h-4" />
                </div>

                {/* Child Label */}
                <span className="flex-1 text-left truncate">{child.label}</span>

                {/* Active Arrow */}
                {childActive && (
                  <ChevronRight className="w-4 h-4 flex-shrink-0" />
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* SUBMENU POPOVER - Collapsed Sidebar */}
      {showPopover && (() => {
        const buttonElement = document.querySelector(`button[aria-label="${item.label}"]`);
        const rect = buttonElement?.getBoundingClientRect();

        return (
          <div
            className="fixed bg-white rounded-lg shadow-xl border border-slate-200 py-2 px-1 min-w-[200px] z-[100]"
            style={{
              left: `${(rect?.right || 0) + 8}px`,
              top: `${rect?.top || 0}px`,
            }}
            onMouseEnter={handlePopoverEnter}
            onMouseLeave={handlePopoverHide}
          >
            {/* Popover Header */}
            <div className="px-3 py-2 border-b border-slate-100 mb-1">
              <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                {item.label}
              </p>
            </div>

            {/* Popover Menu Items */}
            <div className="space-y-0.5">
              {item.children.map((child) => {
                const ChildIcon = child.icon;
                const childActive = location.pathname === child.path;

                return (
                  <button
                    key={child.path}
                    onClick={() => handleMenuClick(child.path, item.label)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 mx-1 rounded-lg text-sm font-medium transition-all duration-200 group relative ${
                      childActive
                        ? colors.active
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }`}
                  >
                    {/* Active Indicator */}
                    {childActive && (
                      <div className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 ${colors.indicator} rounded-r-full`} />
                    )}

                    {/* Child Icon */}
                    <div className="flex-shrink-0 group-hover:scale-110 transition-transform">
                      <ChildIcon className="w-4 h-4" />
                    </div>

                    {/* Child Label */}
                    <span className="flex-1 text-left truncate">{child.label}</span>

                    {/* Active Arrow */}
                    {childActive && (
                      <ChevronRight className="w-4 h-4 flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })()}
    </div>
  );
})}


      </nav>

      {/* Powered By Section */}
      <div className="p-3 border-t border-slate-200">
        {sidebarOpen ? (
          <div className="flex flex-col items-center gap-1">
            <span className="text-[10px] text-slate-400">A Product by</span>
            <a
              href={brandingConfig.companyWebsite}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-80 transition-opacity"
            >
              <img
                src={brandingConfig.companyLogoUrl}
                alt={brandingConfig.companyName}
                className="h-12 max-w-[140px] object-contain"
              />
            </a>
            <span className="text-[10px] text-slate-400 mt-1">
              {`${brandingConfig.productName} ${brandingConfig.productVersion}`}
            </span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <a
              href={brandingConfig.companyWebsite}
              target="_blank"
              rel="noopener noreferrer"
              className="flex justify-center hover:opacity-80 transition-opacity"
              title={`A Product by ${brandingConfig.companyName}`}
            >
              <img
                src={brandingConfig.companyLogoUrl}
                alt={brandingConfig.companyName}
                className="h-8 max-w-[40px] object-contain"
              />
            </a>
            <span className="text-[9px] text-slate-400" title={`${brandingConfig.productName} ${brandingConfig.productVersion}`}>
              {brandingConfig.productVersion}
            </span>
          </div>
        )}
      </div>
    </aside>
  );
}